/**
 * QUIPWA — data access for the local `notes` table (device DB, migration 2)
 *
 * Plain SQL, shared by LocalNotesRepository (what the UI does) and
 * NotesDriveSync (what the sync does). Neither talks to SQL directly.
 *
 * Concurrency: every local edit bumps `local_rev`. The sync reads a row,
 * talks to Drive, and then writes back with `updateIf(id, rev, patch)`, which
 * only applies if nobody edited the note meanwhile — so an upload that took
 * a few seconds can never overwrite what the user typed during it.
 */
import type { IDBAdapter, SQLParam } from '../db/db-adapter'
import { fieldsFromMarkdown } from './note-model'

export interface NoteRow {
  id: string
  drive_file_id: string | null
  parent_id: string | null
  folder_id: string | null
  filename: string
  title: string
  type: string
  status: string
  domain: string
  /** JSON array. */
  tags: string
  /** The whole .md file as this device has it. */
  body: string
  /** Last version known to be on Drive: the merge's common ancestor. */
  base: string | null
  base_version: string | null
  /** ms since epoch. */
  modified_at: number
  local_rev: number
  dirty: 0 | 1
  deleted: 0 | 1
}

export type NoteRowPatch = Partial<Omit<NoteRow, 'id'>>

const COLUMNS: ReadonlyArray<keyof NoteRow> = [
  'id', 'drive_file_id', 'parent_id', 'folder_id', 'filename', 'title', 'type', 'status', 'domain',
  'tags', 'body', 'base', 'base_version', 'modified_at', 'local_rev', 'dirty', 'deleted',
]

/** The listing columns (title, type…) derived from a body, ready to spread into a patch. */
export function fieldsPatch(body: string, filename: string): Pick<NoteRow, 'title' | 'type' | 'status' | 'domain' | 'tags'> {
  const f = fieldsFromMarkdown(body, filename)
  return { title: f.title, type: f.type, status: f.status, domain: f.domain, tags: JSON.stringify(f.tags) }
}

export class NotesTable {
  private readonly db: IDBAdapter

  constructor(db: IDBAdapter) {
    this.db = db
  }

  get(id: string): Promise<NoteRow | null> {
    return this.db.queryOne<NoteRow>('SELECT * FROM notes WHERE id = ?', [id])
  }

  getByDriveId(driveFileId: string): Promise<NoteRow | null> {
    return this.db.queryOne<NoteRow>('SELECT * FROM notes WHERE drive_file_id = ?', [driveFileId])
  }

  /** Every note not deleted locally, newest first. */
  listVisible(): Promise<NoteRow[]> {
    return this.db.query<NoteRow>('SELECT * FROM notes WHERE deleted = 0 ORDER BY modified_at DESC')
  }

  listAll(): Promise<NoteRow[]> {
    return this.db.query<NoteRow>('SELECT * FROM notes')
  }

  children(parentId: string): Promise<NoteRow[]> {
    return this.db.query<NoteRow>('SELECT * FROM notes WHERE parent_id = ? AND deleted = 0', [parentId])
  }

  async insert(row: NoteRow): Promise<void> {
    await this.db.run(
      `INSERT INTO notes (${COLUMNS.join(', ')}) VALUES (${COLUMNS.map(() => '?').join(', ')})`,
      COLUMNS.map((c) => row[c] as SQLParam)
    )
  }

  async update(id: string, patch: NoteRowPatch): Promise<void> {
    const keys = Object.keys(patch) as Array<keyof NoteRowPatch>
    if (keys.length === 0) return
    await this.db.run(
      `UPDATE notes SET ${keys.map((k) => `${k} = ?`).join(', ')} WHERE id = ?`,
      [...keys.map((k) => patch[k] as SQLParam), id]
    )
  }

  /**
   * Apply `patch` only if the note's `local_rev` is still `rev`. Returns
   * whether it applied (false: the user edited the note meanwhile).
   */
  async updateIf(id: string, rev: number, patch: NoteRowPatch): Promise<boolean> {
    let applied = false
    await this.db.transaction(async (tx) => {
      const row = await tx.queryOne<{ local_rev: number }>('SELECT local_rev FROM notes WHERE id = ?', [id])
      if (!row || row.local_rev !== rev) return
      const keys = Object.keys(patch) as Array<keyof NoteRowPatch>
      if (keys.length > 0) {
        await tx.run(
          `UPDATE notes SET ${keys.map((k) => `${k} = ?`).join(', ')} WHERE id = ?`,
          [...keys.map((k) => patch[k] as SQLParam), id]
        )
      }
      applied = true
    })
    return applied
  }

  /** A local edit: apply the patch, bump the revision, mark dirty. Returns the new revision. */
  async touch(id: string, patch: NoteRowPatch, now: number): Promise<number> {
    let rev = 0
    await this.db.transaction(async (tx) => {
      const row = await tx.queryOne<{ local_rev: number }>('SELECT local_rev FROM notes WHERE id = ?', [id])
      if (!row) throw new Error(`Nota no encontrada: ${id}`)
      rev = row.local_rev + 1
      const full: NoteRowPatch = { ...patch, local_rev: rev, dirty: 1, modified_at: now }
      const keys = Object.keys(full) as Array<keyof NoteRowPatch>
      await tx.run(
        `UPDATE notes SET ${keys.map((k) => `${k} = ?`).join(', ')} WHERE id = ?`,
        [...keys.map((k) => full[k] as SQLParam), id]
      )
    })
    return rev
  }

  async remove(id: string): Promise<void> {
    await this.db.run('DELETE FROM notes WHERE id = ?', [id])
  }
}
